import { useEffect, useState } from 'react';
import { Download, X, Share } from 'lucide-react';

// Prompts mobile/desktop users to install the app to their home screen.
// Android/Chrome: uses the native beforeinstallprompt. iOS Safari: shows the
// manual "Share → Add to Home Screen" hint (iOS has no install API).
const DISMISS_KEY = 'pwa_install_dismissed_v1';

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)')?.matches ||
  window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
  !/crios|fxios/i.test(window.navigator.userAgent);

export const PWAInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return undefined;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', () => setShow(false));

    // iOS never fires beforeinstallprompt — show the manual hint on Safari.
    let t;
    if (isIos()) t = setTimeout(() => { setIosHint(true); setShow(true); }, 2500);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem(DISMISS_KEY, '1'); };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') setShow(false);
    else dismiss();
    setDeferred(null);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[92%] max-w-md" data-testid="pwa-install-banner">
      <div className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white shadow-xl px-4 py-3">
        <img src="/icon-192.png" alt="PostToSocial" className="w-10 h-10 rounded-lg shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-zinc-900 leading-tight">Add PostToSocial to your Home Screen</p>
          {iosHint ? (
            <p className="text-[12px] text-zinc-500 leading-snug flex items-center gap-1 flex-wrap">
              Tap <Share className="w-3.5 h-3.5 inline text-indigo-600" /> Share, then <span className="font-medium">“Add to Home Screen.”</span>
            </p>
          ) : (
            <p className="text-[12px] text-zinc-500 leading-snug">One tap to launch it like an app — no store needed.</p>
          )}
        </div>
        {!iosHint && (
          <button onClick={install} className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3.5 py-2 transition-colors" data-testid="pwa-install-btn">
            <Download className="w-4 h-4" /> Install
          </button>
        )}
        <button onClick={dismiss} className="shrink-0 text-zinc-400 hover:text-zinc-600 p-1" aria-label="Dismiss" data-testid="pwa-install-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
